import { useEffect, useState, useCallback } from 'react';
import cn from 'classnames';
import { getUsers, toggleInfluencer, deleteUser } from 'api/user';
import { useTranslation } from 'react-i18next';
import { usePageQueryParams } from 'hooks';
import { Table, Spinner, Menu, Icon } from 'components';
import { MenuType } from 'components/Menu';
import { toast } from 'react-toastify';

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [selected, setSelected] = useState<number>(-1);
  const { getPage, setPage } = usePageQueryParams();
  const [resultsTotal, setResultsTotal] = useState({
    count: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState<boolean>(true);
  const page = getPage() || 1;

  const defaultUser =
    'https://the-crane-club.fra1.digitaloceanspaces.com/utils/utils/default-user.jpeg';

  useEffect(() => {
    setLoading(true);
    load();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const load = async () => {
    try {
      const { count, rows, totalPages } = await getUsers(page - 1, 12);
      setResultsTotal({ count, totalPages });
      setUsers(rows);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOnDelete = async (userId: number) => {
    try {
      setSelected(userId);
      await deleteUser(userId);
      toast.success(t('notifications.userDeleted'));
      load();
    } catch (error) {
      console.log(error);
      toast.error(t('notifications.genericError'));
    } finally {
      setSelected(-1);
    }
  };

  const handleUserInfluencer = async (
    uid: number,
    isInfluencer: boolean,
    index: number
  ) => {
    try {
      setSelected(uid);
      await toggleInfluencer(uid, isInfluencer);
      const _users = users;
      _users[index] = { ..._users[index], isInfluencer: isInfluencer };
      setUsers(_users);
      toast.success(t('notifications.genericSuccess'));
    } catch (error) {
      toast.error(t('notifications.genericError'));
    } finally {
      setSelected(-1);
    }
  };

  const renderMenu = useCallback(
    ({ isInfluencer, uid }: User, index: number) => {
      const menu: MenuType[] = [];
      if (!isInfluencer) {
        menu.push({
          label: t('makeInfluencer'),
          icon: 'Approve',
          onClick: () => handleUserInfluencer(uid!, true, index)
        });
      } else
        menu.push({
          label: t('removeInfluencer'),
          icon: 'Reject',
          onClick: () => handleUserInfluencer(uid!, false, index)
        });
      menu.push({
        label: t('delete'),
        icon: 'Trash',
        onClick: () => handleOnDelete(uid!)
      });
      return menu;
    },
    [t, users]
  );

  const renderRows = () => {
    return users.map((user: User, index: number) => (
      <tr
        key={user.uid}
        className="hover:bg-yellow-light transition duration-300"
      >
        <Table.Cell className="cursor-pointer" onClick={() => null}>
          <div className="flex items-center">
            <div className="flex-shrink-0 h-12 w-12 bg-no-repeat bg-contain rounded-full bg-center overflow-hidden border border-solid border-silver-200">
              <img
                src={user?.photoURL || defaultUser}
                onError={(e) => {
                  e.currentTarget.src = defaultUser;
                }}
                alt="Company"
                className="object-contain w-full h-full p-1"
              />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {user?.name ? user?.name : user?.email}
              </div>
            </div>
          </div>
        </Table.Cell>
        <Table.Cell>{user.email}</Table.Cell>
        <Table.Cell>{user.uid}</Table.Cell>
        <Table.Cell>
          <span>
            {user.role || ''}{' '}
            {user.companyName && `${t('at')} ${user?.companyName}`}
          </span>
        </Table.Cell>
        <Table.Cell>{user.phoneNumber}</Table.Cell>
        <Table.Cell>
          {user.isInfluencer ? <Icon name="TickSolid" /> : null}
        </Table.Cell>
        <Table.Cell>
          <Menu
            trigger={
              <Icon
                name="VerticalDots"
                width="16"
                height="16"
                canvasWidth={34}
                canvasHeight={34}
                rounded
                center
                loading={selected === user.uid}
                onClick={() => null}
              />
            }
            items={renderMenu(user, index)}
          />
        </Table.Cell>
      </tr>
    ));
  };

  return (
    <div
      className={cn('py-5 px-10', {
        'animate-pulse pointer-events-none': loading
      })}
    >
      <h3 className="text-2xl">{t('Users')}</h3>
      <div className="flex items-center justify-between h-8 mb-5">
        <div
          className={cn('text-silver text-xs', {
            'bg-silver-light rounded-sm text-transparent w-24 h-4': loading
          })}
        >
          {!loading && (
            <span className="text-gray">
              {t('totalItems', { value: resultsTotal.count })}
            </span>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-6`}>
        {loading ? (
          <Spinner />
        ) : (
          <Table
            page={page}
            setPage={setPage}
            totalPages={resultsTotal.totalPages}
            headerLabels={[
              t('name'),
              t('id'),
              t('email'),
              t('role'),
              t('phone'),
              t('influencer'),
              ''
            ]}
            rows={renderRows()}
          ></Table>
        )}
      </div>
    </div>
  );
};

export default Users;
